import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import Header, { dispatchLogoUpdate } from "../components/Header";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useLocation } from "react-router-dom";
import { useProfile } from "../utils/UseProfile";
import { PencilIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/solid";
import Toast from "../components/Toast";
import { authService, profileService } from "../services/apiService";
import { API_BASE_URL, API_PREFIX, API_ENDPOINTS } from "../constants/config";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../firebase";

const API_URL = `${API_BASE_URL}${API_PREFIX}`;

const isAdmin = (role) =>
  role?.split(",").some((r) => r.trim().toUpperCase() === "ADMIN");

const canManageInvoiceAddress = (role) =>
  role?.split(",").some((r) => {
    const t = r.trim().toUpperCase();
    return t === "ADMIN" || t === "SUPER-ADMIN-JASH";
  });
// ✅ Helper: Build changes object
const buildChanges = (currentData, originalData, extraFields = {}) => {
  let changes = { ...extraFields };
  Object.keys(currentData).forEach((key) => {
    if (key === 'roleResponse') {
      // Special handling for roleResponse object
      if (currentData.roleResponse?.role !== originalData.roleResponse?.role) {
        changes.roleResponse = { role: currentData.roleResponse?.role };
      }
    } else if (currentData[key] !== originalData[key]) {
      changes[key] = currentData[key];
    }
  });
  return changes;
};

// ✅ Helper: Auth headers
const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const Profile = () => {
  const { profile, fetchProfile } = useProfile();
  const location = useLocation();
  const profileData = location.state || {};

  const [userName, setUserName] = useState(profileData.personalDetailsResource?.name || "");
  const [email, setEmail] = useState(profileData.personalDetailsResource?.email || "");
  const [mobile, setMobile] = useState(profileData.personalDetailsResource?.mobile || "");
  const [dateOfBirth, setDob] = useState(profileData.personalDetailsResource?.dateOfBirth || "");
  const [roleResponse, setRoleResponse] = useState(
    profileData.roleResponse?.role || ""
  );
  const [profileImage, setProfileImage] = useState(
    profileData.profileImage || localStorage.getItem("clientLogoDataUrl") || "/logo.png"
  );
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoToast, setLogoToast] = useState(null);
  const logoInputRef = useRef(null);
  const [perDId, setPerDId] = useState(profileData.personalDetailsResource?.perDId || "");
  const [gstin, setGstin] = useState(profileData.gstin);

  // Admin Table State — persist only on refresh, clear on navigation
  const SS_KEY = "profile_users";
  const SS_FILTERS = "profile_filters";
  const SS_NAV_FLAG = "profile_page_active";

  const isPageRefresh = sessionStorage.getItem(SS_NAV_FLAG) === "true";

  const [users, setUsers] = useState(() => {
    if (!isPageRefresh) return [];
    try { return JSON.parse(sessionStorage.getItem(SS_KEY)) || []; } catch { return []; }
  });

  // Filters — persist only on refresh
  const [filters, setFilters] = useState(() => {
    if (!isPageRefresh) return { mobile: "", email: "", status: "", roles: [] };
    try { return JSON.parse(sessionStorage.getItem(SS_FILTERS)) || { mobile: "", email: "", status: "", roles: [] }; } catch { return { mobile: "", email: "", status: "", roles: [] }; }
  });

  // Keep original data for comparison
  const [originalData, setOriginalData] = useState({
    name: profileData.name || "",
    email: profileData.email || "",
    mobile: profileData.mobile || "",
    dateOfBirth: profileData.dateOfBirth || "",
    religion: profileData.religion || "",
    profileImage: profileData.profileImage || "/logo.png",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [editRowIndex, setEditRowIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editRoleDropdownOpen, setEditRoleDropdownOpen] = useState(false);
  const [editRoleSearch, setEditRoleSearch] = useState("");
  const [editRolePos, setEditRolePos] = useState({ top: 0, left: 0, width: 0 });
  const editRoleAnchorRef = useRef(null);
  const [showResetPasswordPopup, setShowResetPasswordPopup] = useState(false);
  const [resetPwForm, setResetPwForm] = useState({ password: "", confirmPassword: "" });
  const [resetPwLoading, setResetPwLoading] = useState(false);

  const [showInvoiceAddressPopup, setShowInvoiceAddressPopup] = useState(false);
  const [invoiceAddressForm, setInvoiceAddressForm] = useState({ address: "", phone: "", email: "" });
  const [invoiceAddressLoading, setInvoiceAddressLoading] = useState(false);
  const [invoiceAddressToast, setInvoiceAddressToast] = useState(null);

  const [resetPwErrors, setResetPwErrors] = useState({ password: "", confirmPassword: "" });

  const validateResetPwField = (field, value) => {
    if (field === "password") {
      setResetPwErrors((p) => ({ ...p, password: value.trim() ? "" : "Password is required" }));
    }
    if (field === "confirmPassword") {
      setResetPwErrors((p) => ({ ...p, confirmPassword: value !== resetPwForm.password ? "Passwords do not match" : "" }));
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwForm.password.trim()) { setResetPwErrors((p) => ({ ...p, password: "Password is required" })); return; }
    if (resetPwForm.password !== resetPwForm.confirmPassword) { setResetPwErrors((p) => ({ ...p, confirmPassword: "Passwords do not match" })); return; }
    setResetPwLoading(true);
    try {
      const userMobile = localStorage.getItem("userName") || mobile;
      const res = await fetch(`${API_URL}/users/reset-password`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ mobileNo: userMobile, newPassword: resetPwForm.password }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.errorMessage || errData?.error || "Failed to reset password");
      }
      const data = await res.json();
      setResetPwToast({ type: "success", message: data.message || "Password reset successfully" });
      setResetPwForm({ password: "", confirmPassword: "" });
      setResetPwErrors({ password: "", confirmPassword: "" });
      setTimeout(() => setShowResetPasswordPopup(false), 1500);
    } catch (err) {
      setResetPwToast({ type: "error", message: err.message || "Failed to reset password" });
    } finally { setResetPwLoading(false); }
  };

  const handleOpenInvoiceAddress = async () => {
    setShowInvoiceAddressPopup(true);
    setInvoiceAddressToast(null);
    try {
      const res = await fetch(`${API_URL}${API_ENDPOINTS.CLIENT_INVOICE_ADDRESS}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setInvoiceAddressForm({ address: data.address || "", phone: data.phone || "", email: data.email || "" });
      }
    } catch {}
  };

  const handleUpdateInvoiceAddress = async () => {
    setInvoiceAddressLoading(true);
    try {
      const res = await fetch(`${API_URL}${API_ENDPOINTS.CLIENT_INVOICE_ADDRESS}`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify(invoiceAddressForm),
      });
      if (!res.ok) throw new Error("Failed to update invoice address");
      setInvoiceAddressToast({ type: "success", message: "Invoice address updated successfully" });
      setTimeout(() => setShowInvoiceAddressPopup(false), 1500);
    } catch (err) {
      setInvoiceAddressToast({ type: "error", message: err.message || "Failed to update" });
    } finally { setInvoiceAddressLoading(false); }
  };

  const [showCreateUserPopup, setShowCreateUserPopup] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ userName: "", password: "", status: "Active", name: "", mobile: "", religion: "", email: "", roles: ["USER"] });
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const AVAILABLE_ROLES = ["ADMIN", "USER", "MANAGER", "STAFF"];
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserToast, setCreateUserToast] = useState(null);
  const [resetPwToast, setResetPwToast] = useState(null);
  const [filterRoleOpen, setFilterRoleOpen] = useState(false);
  const [filterRoleSearch, setFilterRoleSearch] = useState("");

  // OTP states for create user
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [confirmResult, setConfirmResult] = useState(null);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const recaptchaRef = useRef(null);
  const timerRef = useRef(null);

  const getClientInfo = () => {
    try {
      const token = localStorage.getItem("token");
      const payload = JSON.parse(atob(token.split(".")[1]));
      return { clientName: payload.clientName || "", clientCode: payload.clientCode || "" };
    } catch { return { clientName: "", clientCode: "" }; }
  };

  const resetCreateUserForm = () => {
    setCreateUserForm({ userName: "", password: "", status: "Active", name: "", mobile: "", religion: "", email: "", roles: [] });
    setRoleSearch("");
    setRoleDropdownOpen(false);
    setOtpVerified(false);
    setShowOtpInput(false);
    setOtpValue("");
    setOtpError("");
    setConfirmResult(null);
    if (timerRef.current) clearInterval(timerRef.current);
    setResendTimer(0);
  };

  const handleCreateUserChange = (field, value) => {
    setCreateUserForm((p) => ({ ...p, [field]: value }));
    if (field === "userName") { setOtpVerified(false); setShowOtpInput(false); setOtpError(""); }
  };

  const setupRecaptcha = () => {
    if (recaptchaRef.current) { recaptchaRef.current.clear(); recaptchaRef.current = null; }
    recaptchaRef.current = new RecaptchaVerifier(auth, "create-user-recaptcha", { size: "invisible", callback: () => {} });
    return recaptchaRef.current;
  };

  const sendCreateUserOtp = async () => {
    const mobile = createUserForm.userName.trim();
    if (!/^\d{10}$/.test(mobile)) { setOtpError("Enter a valid 10-digit mobile number"); return; }
    setOtpError(""); setOtpLoading(true);
    try {
      const verifier = setupRecaptcha();
      const result = await signInWithPhoneNumber(auth, `+91${mobile}`, verifier);
      setConfirmResult(result);
      setShowOtpInput(true);
      setResendTimer(30);
      timerRef.current = setInterval(() => {
        setResendTimer((t) => { if (t <= 1) { clearInterval(timerRef.current); return 0; } return t - 1; });
      }, 1000);
    } catch (err) {
      setOtpError(err.message || "Failed to send OTP");
      recaptchaRef.current = null;
    } finally { setOtpLoading(false); }
  };

  const verifyCreateUserOtp = async () => {
    if (otpValue.length !== 6) { setOtpError("Enter 6-digit OTP"); return; }
    setOtpError("");
    try {
      await confirmResult.confirm(otpValue);
      setOtpVerified(true);
      setShowOtpInput(false);
    } catch { setOtpError("Invalid OTP"); setOtpValue(""); }
  };

  const handleCreateUserSubmit = async () => {
    if (!createUserForm.userName.trim() || !/^\d{10}$/.test(createUserForm.userName)) { setCreateUserToast({ type: "error", message: "Mobile Number must be exactly 10 digits" }); return; }
    //if (!otpVerified) { setCreateUserToast({ type: "error", message: "Please verify mobile number with OTP" }); return; }
    if (!createUserForm.password.trim()) { setCreateUserToast({ type: "error", message: "Password is required" }); return; }
    if (!createUserForm.name.trim()) { setCreateUserToast({ type: "error", message: "Full Name is required" }); return; }
    if (!createUserForm.email.trim()) { setCreateUserToast({ type: "error", message: "Email is required" }); return; }
    if (createUserForm.roles.length === 0) { setCreateUserToast({ type: "error", message: "At least one role is required" }); return; }
    setCreateUserLoading(true);
    try {
      const clientInfo = getClientInfo();
      await authService.addUser({
        client: { clientName: clientInfo.clientName, clientCode: clientInfo.clientCode },
        login: { mobileNo: createUserForm.userName, password: createUserForm.password, status: createUserForm.status },
        personalDetails: { name: createUserForm.name, mobile: createUserForm.userName, religion: createUserForm.religion, email: createUserForm.email },
        address: {},
        roles: createUserForm.roles.map((r) => ({ role: r })),
      });
      setCreateUserToast({ type: "success", message: "User created successfully!" });
      resetCreateUserForm();
      setTimeout(() => setShowCreateUserPopup(false), 1500);
    } catch (err) {
      const msg = err.response?.data?.errorMessage || err.response?.data?.error || err.message || "Failed to create user";
      setCreateUserToast({ type: "error", message: msg });
    } finally { setCreateUserLoading(false); }
  };

  // Fetch client logo on mount
  useEffect(() => {
    profileService.getClientLogo()
      .then((blob) => {
        if (!blob || blob.size === 0) return;
        const reader = new FileReader();
        reader.onloadend = () => {
          dispatchLogoUpdate(reader.result);
          setProfileImage(reader.result);
        };
        reader.readAsDataURL(blob);
      })
      .catch(() => {});
  }, []);

  // ✅ Admin logo remove
  const handleLogoRemove = async () => {
    setLogoUploading(true);
    try {
      await profileService.deleteClientLogo();
      dispatchLogoUpdate(null);
      setProfileImage("/logo.png");
      setLogoToast({ type: "success", message: "Logo removed. Default logo restored." });
    } catch {
      setLogoToast({ type: "error", message: "Failed to remove logo" });
    } finally {
      setLogoUploading(false);
    }
  };

  // ✅ Admin logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"];
    if (!allowed.includes(file.type)) {
      setLogoToast({ type: "error", message: "Only PNG, JPG, SVG or WebP images are allowed" });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoToast({ type: "error", message: "Image must be under 2MB" });
      return;
    }
    setLogoUploading(true);
    try {
      await profileService.uploadClientLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        dispatchLogoUpdate(reader.result);
        setProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
      setLogoToast({ type: "success", message: "Logo updated successfully!" });
    } catch {
      setLogoToast({ type: "error", message: "Failed to upload logo" });
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  // ✅ Search
  const handleSearch = async () => {
    try {
      setIsSearching(true);
      setUsers([]);

      const searchBody = {
        loginRequest: {
          mobile: filters.mobile || null,
          status: filters.status || null,
        },
        personalDetailsRequest: {
          email: filters.email || null,
        },
        roleRequest: {
          role: filters.roles?.length > 0 ? filters.roles.join(",") : null,
        }
      };

      const response = await fetch(`${API_URL}${API_ENDPOINTS.SEARCH_USERS}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(searchBody),
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      const list = Array.isArray(data) ? data : data.body;
      const result = Array.isArray(list) ? list : [];
      setUsers(result);
      sessionStorage.setItem(SS_KEY, JSON.stringify(result));
      sessionStorage.setItem(SS_FILTERS, JSON.stringify(filters));
    } catch (err) {
      console.error("Error fetching users:", err);
      setUsers([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (profile) {
      setUserName(profile.personalDetailsResource?.name || "");
      setEmail(profile.personalDetailsResource?.email || "");
      setMobile(profile.personalDetailsResource?.mobile || "");
      setDob(profile.personalDetailsResource?.dateOfBirth || "");
      setRoleResponse(profile.roleResponse?.role || "");
      setGstin(profile.gstin || "");
    }
  }, [profile]);

  // Set nav flag on mount, clear on unmount (navigation away)
  useEffect(() => {
    sessionStorage.setItem(SS_NAV_FLAG, "true");
    return () => {
      sessionStorage.removeItem(SS_NAV_FLAG);
      sessionStorage.removeItem(SS_KEY);
      sessionStorage.removeItem(SS_FILTERS);
    };
  }, []);

  // ✅ Save User Info
  const handleUserInfoSave = async () => {
    setIsSaving(true);
    setSuccessMessage("");

    const currentData = { name: userName, email, mobile, dateOfBirth };
    const changes = buildChanges(currentData, originalData);

    if (Object.keys(changes).length === 0) {
      setIsSaving(false);
      setSuccessMessage("No changes to save.");
      return;
    }

    try {
      const userNameHeader = localStorage.getItem("userName");
      const res = await fetch(
        `${API_URL}${API_ENDPOINTS.UPDATE_PROFILE(userNameHeader)}`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify(changes),
        }
      );

      if (!res.ok) throw new Error("Failed to update profile");
      setSuccessMessage("✅ User information saved successfully!");
    } catch (err) {
      console.error(err);
      setSuccessMessage("❌ Failed to save user information.");
    } finally {
      setIsSaving(false);
    }
  };

  // ✅ Edit Input Change
 const handleInputChange = (e) => {
  const { name, value } = e.target;
  console.log("Changed:", name, value);
  setEditFormData((prev) => ({ ...prev, [name]: value }));
};
  

  const handleEditClick = (index, user) => {
    setEditRowIndex(index);
    setEditFormData({
      ...user,
      name: user.personalDetailsResource?.name || "",
      email: user.personalDetailsResource?.email || "",
      mobile: user.personalDetailsResource?.mobile || "",
      password: "",
      gstin: user.gstin || "",
      role: user.roleResponse?.role || "",
      status: user.loginInfoResponse?.status || "",
      perDId: user.personalDetailsResource?.perDId || ""
    });
    setEditRoleSearch("");
    setEditRoleDropdownOpen(false);
  };

  // ✅ Save Edited Row
  const handleRowSave = async () => {
    setIsSaving(true);
    setSuccessMessage("");

    try {
      const changes = {
        personalDetailsRequest: {
          name: editFormData.name,
          email: editFormData.email,
          mobile: editFormData.mobile,
        },
        loginRequest: {
          status: editFormData.status,
          ...(editFormData.password && { password: editFormData.password }),
        },
        roleRequest: {
          role: editFormData.role,
        },
        gstin: editFormData.gstin || null,
      };

      const res = await fetch(
        `${API_URL}${API_ENDPOINTS.UPDATE_USER(editFormData.perDId)}`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify(changes),
        }
      );

      if (!res.ok) throw new Error("Failed to update user");

      // Update local state
      setUsers((prev) =>
        prev.map((user, idx) =>
          idx === editRowIndex ? {
            ...user,
            personalDetailsResource: {
              ...user.personalDetailsResource,
              name: editFormData.name,
              email: editFormData.email,
              mobile: editFormData.mobile,
            },
            loginInfoResponse: {
              ...user.loginInfoResponse,
              status: editFormData.status
            },
            roleResponse: {
              ...user.roleResponse,
              role: editFormData.role
            },
            gstin: editFormData.gstin
          } : user
        )
      );

      setCreateUserToast({ type: "success", message: "User information saved successfully!" });
      fetchProfile();
      handleSearch();
    } catch (err) {
      console.error(err);
      setCreateUserToast({ type: "error", message: "Failed to save user information." });
    } finally {
      setIsSaving(false);
      setEditRowIndex(null);
    }
  };

  const handleCancel = () => {
    setEditRowIndex(null);
    setEditFormData({});
  };

  return (
    <div className="h-screen overflow-y-auto bg-gradient-to-br from-blue-50 to-teal-50 p-6 relative flex flex-col">
      {/* ✅ Overlay when saving */}
      {isSaving && (
        <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex flex-col items-center justify-center z-50">
          <div className="bg-white rounded-lg px-6 py-4 shadow-lg flex flex-col items-center">
            <div className="loader border-t-4 border-blue-500 rounded-full w-12 h-12 mb-4 animate-spin"></div>
            <p className="text-lg font-semibold text-gray-700">Saving...</p>
          </div>
        </div>
      )}

      <Header showSearch={false} showCart={false} />

      {/* User Info Card */}
      <section className="mt-6 bg-white rounded-lg p-6 shadow-md border border-gray-200 ">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-blue-800">
            👤 User Information
          </h2>
          {successMessage && (
            <p className="mb-4 text-green-600 font-medium">{successMessage}</p>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setResetPwForm({ password: "", confirmPassword: "" }); setShowResetPasswordPopup(true); }}
              className="flex items-center gap-1 text-sm font-semibold text-red-500 hover:text-red-600 transition">
              <i className="fas fa-key text-xs" /> Reset Password
            </button>
            {canManageInvoiceAddress(roleResponse) && (
              <button
                onClick={handleOpenInvoiceAddress}
                className="flex items-center gap-1 text-sm font-semibold text-indigo-500 hover:text-indigo-600 transition">
                <i className="fas fa-map-marker-alt text-xs" /> Invoice Address
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative w-32 h-32 group">
            <img
              src={profileImage}
              alt="Profile"
              className="w-full h-full object-contain rounded-full border-2 border-blue-300 bg-white p-1"
              onError={(e) => { e.target.src = "/logo.png"; }}
            />
            {logoUploading && (
              <div className="absolute inset-0 rounded-full bg-white bg-opacity-70 flex items-center justify-center">
                <i className="fas fa-spinner fa-spin text-blue-500 text-xl" />
              </div>
            )}
            {isAdmin(roleResponse) && !logoUploading && (
              <>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <div className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    title="Change logo"
                    className="w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shadow-lg transition"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  {profileImage !== "/logo.png" && (
                    <button
                      onClick={handleLogoRemove}
                      title="Remove logo"
                      className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
            <div>
              <label className="text-sm text-gray-600 block mb-1">Name</label>
              <input
                type="text"
                value={userName}
                readOnly
                className="w-full border px-4 py-2 rounded-md text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 block mb-1">Email</label>
              <input
                type="email"
                value={email}
                readOnly
                className="w-full border px-4 py-2 rounded-md text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 block mb-1">
                Mobile Number
              </label>
              <input
                type="tel"
                value={mobile}
                readOnly
                className="w-full border px-4 py-2 rounded-md text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 block mb-1">GSTIN</label>
              <input
                type="text"
                value={gstin || "-"}
                readOnly
                className="w-full border px-4 py-2 rounded-md text-sm bg-gray-50 text-gray-700 cursor-not-allowed font-mono tracking-wide"
              />
            </div>
          </div>
        </div>
        {logoToast && (
          <div className="mt-3">
            <Toast type={logoToast.type} message={logoToast.message} onClose={() => setLogoToast(null)} />
          </div>
        )}
      </section>

      {isAdmin(roleResponse) && (
        <>
          {/* 🔍 Filter Section */}
          <section className="mt-8 bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <h2 className="text-md font-semibold text-blue-800 mb-3">
              🔍 Filter Users
            </h2>
            <div className="flex flex-wrap gap-4">
              <input
                type="text"
                name="mobile"
                value={filters.mobile}
                onChange={handleFilterChange}
                placeholder="Mobile No"
                disabled={isSearching}
                className="border px-3 py-2 rounded w-[200px]"
              />
              <input
                type="email"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
                placeholder="Email"
                disabled={isSearching}
                className="border px-3 py-2 rounded w-[200px]"
              />
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                disabled={isSearching}
                className="border px-3 py-2 rounded w-[150px]"
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              {/* Role multi-select dropdown */}
              <div className="relative w-[200px]">
                <div className="flex flex-wrap gap-1 pl-2 pr-7 py-1.5 border rounded min-h-[38px] cursor-text w-full bg-white"
                  onClick={() => setFilterRoleOpen(true)}>
                  {(filters.roles || []).map((r) => (
                    <span key={r} className="flex items-center gap-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                      {r}
                      <button type="button" onClick={(e) => { e.stopPropagation(); setFilters((p) => { const u = { ...p, roles: p.roles.filter((x) => x !== r) }; sessionStorage.setItem(SS_FILTERS, JSON.stringify(u)); return u; }); }}
                        className="text-blue-400 hover:text-blue-600"><i className="fas fa-times" style={{fontSize:"7px"}} /></button>
                    </span>
                  ))}
                  <input type="text" value={filterRoleSearch}
                    onChange={(e) => { setFilterRoleSearch(e.target.value); setFilterRoleOpen(true); }}
                    onFocus={() => setFilterRoleOpen(true)}
                    onBlur={() => setTimeout(() => setFilterRoleOpen(false), 150)}
                    placeholder={(filters.roles || []).length === 0 ? "Roles" : ""}
                    disabled={isSearching}
                    className="flex-1 min-w-[40px] text-sm outline-none bg-transparent" />
                </div>
                <span onMouseDown={(e) => { e.preventDefault(); setFilterRoleOpen((o) => !o); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </span>
                {filterRoleOpen && (() => {
                  const current = filters.roles || [];
                  const filtered = AVAILABLE_ROLES.filter((r) => !current.includes(r) && r.toLowerCase().includes(filterRoleSearch.toLowerCase()));
                  return filtered.length > 0 ? (
                    <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-32 overflow-y-auto text-sm">
                      {filtered.map((r) => (
                        <li key={r} onMouseDown={() => { setFilters((p) => { const u = { ...p, roles: [...(p.roles || []), r] }; sessionStorage.setItem(SS_FILTERS, JSON.stringify(u)); return u; }); setFilterRoleSearch(""); }}
                          className="px-3 py-1.5 cursor-pointer hover:bg-blue-50 text-gray-700">{r}</li>
                      ))}
                    </ul>
                  ) : null;
                })()}
              </div>
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className={`flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-500 
                  hover:from-indigo-600 hover:to-indigo-500 text-white font-semibold px-6 py-2 
                  rounded-full shadow-md transition duration-200
                  ${isSearching ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <i className="fas fa-search"></i> Search
              </button>
            </div>
          </section>

          {/* 🛡️ Admin Section Table */}
          <section className="mt-6 bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-blue-800">
                🛡️ Admin Role Management
              </h2>
              <button onClick={() => { resetCreateUserForm(); setShowCreateUserPopup(true); }}
                className="flex items-center gap-1 text-sm font-semibold text-green-600 hover:text-green-700 transition">
                <span className="w-4 h-4 rounded-full bg-green-100 border border-green-300 flex items-center justify-center">
                  <i className="fas fa-plus" style={{fontSize: "8px"}} />
                </span>
                Create User
              </button>
            </div>

            {/* GSTIN Row */}
            <div className="overflow-x-auto overflow-y-auto max-h-96">
              <table className="min-w-full text-sm text-gray-800 border border-blue-200 rounded-lg overflow-hidden">
                <thead className="bg-gradient-to-r from-blue-200 via-indigo-100 to-teal-100 text-blue-900 uppercase text-xs tracking-wide sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 border">Name</th>
                    <th className="px-4 py-2 border">Email</th>
                    <th className="px-4 py-2 border">Mobile</th>
                    <th className="px-4 py-2 border">Password</th>
                    <th className="px-4 py-2 border">GSTIN</th>
                    <th className="px-4 py-2 border">Role</th>
                    <th className="px-4 py-2 border">Status</th>
                    <th className="px-4 py-2 border">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => (
                    <tr key={idx} className="text-gray-700">
                      <td className="px-4 py-2 border">
                        {editRowIndex === idx ? (
                          <input
                            type="text"
                            name="name"
                            value={editFormData.name || ""}
                            onChange={handleInputChange}
                            className="border p-1 w-full"
                          />
                        ) : (
                          user.personalDetailsResource?.name
                        )}
                      </td>

                      <td className="px-4 py-2 border">
                        {editRowIndex === idx ? (
                          <input
                            type="email"
                            name="email"
                            value={editFormData.email || ""}
                            onChange={handleInputChange}
                            className="border p-1 w-full"
                          />
                        ) : (
                          user.personalDetailsResource?.email
                        )}
                      </td>

                      <td className="px-4 py-2 border">
                        {editRowIndex === idx ? (
                          <input
                            type="text"
                            name="mobile"
                            value={editFormData.mobile || ""}
                            onChange={handleInputChange}
                            className="border p-1 w-full"
                          />
                        ) : (
                          user.personalDetailsResource?.mobile
                        )}
                      </td>

                      <td className="px-4 py-2 border">
                        {editRowIndex === idx ? (
                          <input
                            type="password"
                            name="password"
                            value={editFormData.password || ""}
                            onChange={handleInputChange}
                            placeholder="New password"
                            className="border p-1 w-full"
                          />
                        ) : (
                          "••••••"
                        )}
                      </td>

                      <td className="px-4 py-2 border">
                        {editRowIndex === idx && user.roleResponse?.role?.toLowerCase().includes("admin") ? (
                          <input
                            type="text"
                            name="gstin"
                            value={editFormData.gstin || ""}
                            onChange={handleInputChange}
                            placeholder="GSTIN"
                            maxLength={15}
                            className="border p-1 w-full font-mono text-xs uppercase"
                          />
                        ) : (
                          <span className="font-mono text-xs">{user.gstin || "-"}</span>
                        )}
                      </td>

                      <td className="px-4 py-2 border">
                        {editRowIndex === idx ? (
                          <div className="relative" ref={editRoleAnchorRef}>
                            <div className="flex flex-wrap gap-0.5 pl-1 pr-6 py-1 border rounded min-h-[30px] cursor-text w-full"
                              onClick={() => {
                                const rect = editRoleAnchorRef.current?.getBoundingClientRect();
                                if (rect) setEditRolePos({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX, width: rect.width });
                                setEditRoleDropdownOpen(true);
                              }}>
                              {(editFormData.role || "").split(",").filter(Boolean).map((r) => (
                                <span key={r} className="flex items-center gap-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium px-1 py-0.5 rounded-full">
                                  {r.trim()}
                                  <button type="button" onClick={(e) => { e.stopPropagation(); const roles = editFormData.role.split(",").filter((x) => x.trim() !== r.trim()).join(","); setEditFormData((p) => ({ ...p, role: roles })); }}
                                    className="text-blue-400 hover:text-blue-600"><i className="fas fa-times" style={{fontSize:"7px"}} /></button>
                                </span>
                              ))}
                              <input type="text" value={editRoleSearch}
                                onChange={(e) => {
                                  setEditRoleSearch(e.target.value);
                                  const rect = editRoleAnchorRef.current?.getBoundingClientRect();
                                  if (rect) setEditRolePos({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX, width: rect.width });
                                  setEditRoleDropdownOpen(true);
                                }}
                                onFocus={() => {
                                  const rect = editRoleAnchorRef.current?.getBoundingClientRect();
                                  if (rect) setEditRolePos({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX, width: rect.width });
                                  setEditRoleDropdownOpen(true);
                                }}
                                onBlur={() => setTimeout(() => setEditRoleDropdownOpen(false), 150)}
                                placeholder={!editFormData.role ? "Roles..." : ""}
                                className="flex-1 min-w-[40px] text-xs outline-none bg-transparent" />
                            </div>
                            <span onMouseDown={(e) => {
                                e.preventDefault();
                                const rect = editRoleAnchorRef.current?.getBoundingClientRect();
                                if (rect) setEditRolePos({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX, width: rect.width });
                                setEditRoleDropdownOpen((o) => !o);
                              }}
                              className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer">
                              <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                            </span>
                          </div>
                        ) : (
                          user.roleResponse?.role || "No role"
                        )}
                      </td>

                      <td className="px-4 py-2 border">
                        {editRowIndex === idx ? (
                          <select
                            name="status"
                            value={editFormData.status || ""}
                            onChange={handleInputChange}
                            className="border p-1 w-full"
                          >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select>
                        ) : (
                          <span
                            className={`px-2 py-1 rounded-full text-white text-xs ${
                              user.loginInfoResponse?.status === "Active"
                                ? "bg-green-500"
                                : "bg-red-400"
                            }`}
                          >
                            {user.loginInfoResponse?.status}
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-2 border">
                        {editRowIndex === idx ? (
                          <div className="flex items-center justify-center gap-3">
                            <CheckIcon
                              onClick={handleRowSave}
                              className="h-5 w-5 text-green-600 cursor-pointer hover:scale-110 transition-transform duration-200"
                              title="Save"
                            />
                            <XMarkIcon
                              onClick={handleCancel}
                              className="h-5 w-5 text-red-600 cursor-pointer hover:scale-110 transition-transform duration-200"
                              title="Cancel"
                            />
                          </div>
                        ) : (
                          <PencilIcon
                            onClick={() => handleEditClick(idx, user)}
                            className="h-5 w-5 text-blue-600 cursor-pointer hover:scale-110 transition-transform duration-200 mx-auto block"
                            title="Edit"
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Manage all users in one place.
            </p>
          </section>
        </>
      )}
      {createUserToast && <Toast type={createUserToast.type} message={createUserToast.message} onClose={() => setCreateUserToast(null)} />}

      {/* Reset Password Popup */}
      {showResetPasswordPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-blue-200 w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 rounded-t-xl">
              <h3 className="font-bold text-blue-800 text-base"><i className="fas fa-key mr-2" />Reset Password</h3>
              <button onClick={() => { setShowResetPasswordPopup(false); setResetPwToast(null); setResetPwErrors({ password: "", confirmPassword: "" }); }} className="text-gray-600 hover:text-gray-800"><i className="fas fa-times text-lg" /></button>
            </div>
            {resetPwToast && <div className="px-5 pt-3"><Toast type={resetPwToast.type} message={resetPwToast.message} onClose={() => setResetPwToast(null)} /></div>}
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Client Code</label>
                <input type="text" value={getClientInfo().clientCode} readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Mobile No</label>
                <input type="text" value={localStorage.getItem("userName") || mobile} readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">New Password *</label>
                <input type="password" value={resetPwForm.password}
                  onChange={(e) => setResetPwForm((p) => ({ ...p, password: e.target.value }))}
                  onBlur={(e) => validateResetPwField("password", e.target.value)}
                  placeholder="Enter new password"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${resetPwErrors.password ? "border-red-400" : "border-blue-300"}`} />
                {resetPwErrors.password && <p className="text-red-400 text-xs mt-0.5">{resetPwErrors.password}</p>}
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Confirm Password *</label>
                <input type="password" value={resetPwForm.confirmPassword}
                  onChange={(e) => setResetPwForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                  onBlur={(e) => validateResetPwField("confirmPassword", e.target.value)}
                  placeholder="Confirm new password"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${resetPwErrors.confirmPassword ? "border-red-400" : "border-blue-300"}`} />
                {resetPwErrors.confirmPassword && <p className="text-red-400 text-xs mt-0.5">{resetPwErrors.confirmPassword}</p>}
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                <button onClick={() => { setShowResetPasswordPopup(false); setResetPwToast(null); setResetPwErrors({ password: "", confirmPassword: "" }); }}
                  className="px-4 py-2 border border-gray-300 rounded-full text-gray-600 hover:bg-gray-50 text-sm font-semibold">Cancel</button>
                <button onClick={handleResetPassword} disabled={resetPwLoading}
                  className={`px-4 py-2 rounded-full text-white font-semibold text-sm shadow transition ${resetPwLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"}`}>
                  {resetPwLoading ? <><i className="fas fa-spinner fa-spin mr-1" />Resetting...</> : <><i className="fas fa-key mr-1" />Reset</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Popup */}
      {showCreateUserPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-blue-200 w-full max-w-2xl">
            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 rounded-t-xl">
              <h3 className="font-bold text-blue-800 text-lg"><i className="fas fa-user-plus mr-2" />Create User</h3>
              <button onClick={() => setShowCreateUserPopup(false)} className="text-gray-600 hover:text-gray-800"><i className="fas fa-times text-xl" /></button>
            </div>
            <div className="p-5 space-y-5">
              {/* Client Info (readonly) */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Client Info</h4>
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" value={getClientInfo().clientName} readOnly
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
                  <input type="text" value={getClientInfo().clientCode} readOnly
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
                </div>
              </div>
              {/* Login Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Login Info</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="relative">
                    <input type="text" placeholder="Mobile Number *" value={createUserForm.userName}
                      //disabled={otpVerified}
                      onChange={(e) => handleCreateUserChange("userName", e.target.value.replace(/\D/g, "").slice(0, 10))}
                      className={`w-full pl-3 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${otpVerified ? "border-green-400 bg-green-50" : "border-blue-300"}`} />
                    {/*{otpVerified ? (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500"><i className="fas fa-check-circle text-sm" /></span>
                    ) : (
                      <button type="button" onClick={sendCreateUserOtp} disabled={otpLoading}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50">
                        {otpLoading ? <i className="fas fa-spinner fa-spin" style={{fontSize:"9px"}} /> : <i className="fas fa-paper-plane" style={{fontSize:"9px"}} />}
                      </button>
                    )}*/}
                  </div>
                  <input type="password" placeholder="Password *" value={createUserForm.password}
                    onChange={(e) => handleCreateUserChange("password", e.target.value)}
                    className="px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                  <select value={createUserForm.status}
                    onChange={(e) => handleCreateUserChange("status", e.target.value)}
                    className="px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none">
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                {otpError && <p className="text-red-400 text-xs mt-1">{otpError}</p>}
                {otpVerified && <p className="text-green-500 text-xs mt-1"><i className="fas fa-check-circle mr-1" />Mobile verified</p>}
                {showOtpInput && !otpVerified && (
                  <div className="flex items-center gap-2 mt-2">
                    <input type="text" value={otpValue} maxLength={6}
                      onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      className="w-36 px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                    <button onClick={verifyCreateUserOtp}
                      className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg">Verify</button>
                    {resendTimer > 0 ? (
                      <span className="text-xs text-gray-400">Resend in {resendTimer}s</span>
                    ) : (
                      <button onClick={sendCreateUserOtp} disabled={otpLoading}
                        className="text-xs text-blue-500 hover:text-blue-600 font-semibold disabled:opacity-50">Resend</button>
                    )}
                  </div>
                )}
              </div>
            {/* <div id="create-user-recaptcha" /> */}
              {/* Personal Details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Personal Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Full Name *" value={createUserForm.name}
                    onChange={(e) => handleCreateUserChange("name", e.target.value)}
                    className="px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                  <input type="text" placeholder="Mobile" value={createUserForm.userName} readOnly
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
                  <input type="text" placeholder="Religion" value={createUserForm.religion}
                    onChange={(e) => handleCreateUserChange("religion", e.target.value)}
                    className="px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                  <input type="email" placeholder="Email *" value={createUserForm.email}
                    onChange={(e) => handleCreateUserChange("email", e.target.value)}
                    className="px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
                </div>
              </div>
              {/* Roles */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Roles</h4>
                <div className="relative" style={{width: "208px"}}>
                  <div className="flex flex-wrap gap-1 pl-3 pr-7 py-2 border border-blue-300 rounded-lg min-h-[38px] cursor-text w-full"
                    onClick={() => setRoleDropdownOpen(true)}>
                    {createUserForm.roles.map((r) => (
                      <span key={r} className="flex items-center gap-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                        {r}
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleCreateUserChange("roles", createUserForm.roles.filter((x) => x !== r)); }}
                          className="text-blue-400 hover:text-blue-600"><i className="fas fa-times" style={{fontSize: "8px"}} /></button>
                      </span>
                    ))}
                    <input type="text" value={roleSearch}
                      onChange={(e) => { setRoleSearch(e.target.value); setRoleDropdownOpen(true); }}
                      onFocus={() => setRoleDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setRoleDropdownOpen(false), 150)}
                      placeholder={createUserForm.roles.length === 0 ? "Select roles..." : ""}
                      className="flex-1 min-w-[60px] text-sm outline-none bg-transparent" />
                  </div>
                  <span onMouseDown={(e) => { e.preventDefault(); setRoleDropdownOpen((o) => !o); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </span>
                  {roleDropdownOpen && (() => {
                    const filtered = AVAILABLE_ROLES.filter((r) =>
                      !createUserForm.roles.includes(r) && r.toLowerCase().includes(roleSearch.toLowerCase())
                    );
                    return filtered.length > 0 ? (
                      <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-32 overflow-y-auto text-sm">
                        {filtered.map((r) => (
                          <li key={r} onMouseDown={() => { handleCreateUserChange("roles", [...createUserForm.roles, r]); setRoleSearch(""); }}
                            className="px-3 py-1.5 cursor-pointer hover:bg-blue-50 text-gray-700">{r}</li>
                        ))}
                      </ul>
                    ) : null;
                  })()}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                <button onClick={() => setShowCreateUserPopup(false)}
                  className="px-5 py-2 border border-gray-300 rounded-full text-gray-600 hover:bg-gray-50 text-sm font-semibold">Cancel</button>
                <button onClick={handleCreateUserSubmit} disabled={createUserLoading}
                  className={`px-5 py-2 rounded-full text-white font-semibold text-sm shadow transition ${createUserLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
                  {createUserLoading ? <><i className="fas fa-spinner fa-spin mr-1" />Creating...</> : <><i className="fas fa-save mr-1" />Create User</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Invoice Address Popup */}
      {showInvoiceAddressPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-blue-200 w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 rounded-t-xl">
              <h3 className="font-bold text-blue-800 text-base"><i className="fas fa-map-marker-alt mr-2" />Invoice Address</h3>
              <button onClick={() => setShowInvoiceAddressPopup(false)} className="text-gray-600 hover:text-gray-800"><i className="fas fa-times text-lg" /></button>
            </div>
            {invoiceAddressToast && <div className="px-5 pt-3"><Toast type={invoiceAddressToast.type} message={invoiceAddressToast.message} onClose={() => setInvoiceAddressToast(null)} /></div>}
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Address</label>
                <textarea
                  rows={3}
                  value={invoiceAddressForm.address}
                  onChange={(e) => setInvoiceAddressForm((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Enter store address"
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none resize-none" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Phone</label>
                <input
                  type="text"
                  value={invoiceAddressForm.phone}
                  onChange={(e) => setInvoiceAddressForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="Enter phone number"
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Email</label>
                <input
                  type="email"
                  value={invoiceAddressForm.email}
                  onChange={(e) => setInvoiceAddressForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                <button onClick={() => setShowInvoiceAddressPopup(false)}
                  className="px-4 py-2 border border-gray-300 rounded-full text-gray-600 hover:bg-gray-50 text-sm font-semibold">Cancel</button>
                <button onClick={handleUpdateInvoiceAddress} disabled={invoiceAddressLoading}
                  className={`px-4 py-2 rounded-full text-white font-semibold text-sm shadow transition ${invoiceAddressLoading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600"}`}>
                  {invoiceAddressLoading ? <><i className="fas fa-spinner fa-spin mr-1" />Updating...</> : <><i className="fas fa-save mr-1" />Update</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Role dropdown portal */}
      {editRoleDropdownOpen && (() => {
        const current = (editFormData.role || "").split(",").map((r) => r.trim()).filter(Boolean);
        const filtered = AVAILABLE_ROLES.filter((r) => !current.includes(r) && r.toLowerCase().includes(editRoleSearch.toLowerCase()));
        return filtered.length > 0 ? ReactDOM.createPortal(
          <ul style={{ position: "absolute", top: editRolePos.top, left: editRolePos.left, width: editRolePos.width, zIndex: 9999 }}
            className="bg-white border border-blue-200 rounded shadow-lg max-h-40 overflow-y-auto text-xs">
            {filtered.map((r) => (
              <li key={r} onMouseDown={() => { const newRole = current.length ? `${editFormData.role},${r}` : r; setEditFormData((p) => ({ ...p, role: newRole })); setEditRoleSearch(""); setEditRoleDropdownOpen(false); }}
                className="px-2 py-1.5 cursor-pointer hover:bg-blue-50 text-gray-700">{r}</li>
            ))}
          </ul>,
          document.body
        ) : null;
      })()}
    </div>
  );
};

export default Profile;